/**
 * AudioBus
 *
 * A single audio routing bus with volume, mute, solo,
 * filter chain, and send functionality.
 *
 * Buses form a hierarchical tree rooted at the Master bus.
 * Each bus has a GainNode for volume, optional BiquadFilterNodes,
 * and AnalyserNode for metering.
 *
 * @module @engine/audio/AudioBus
 */

import type { AudioBusConfig, AudioFilterConfig, AudioBusState } from './types';

export class AudioBus {
  readonly id: string;
  name: string;
  parentBusId: string | null;

  private _ctx: AudioContext;
  private _gainNode: GainNode;
  private _muteNode: GainNode;
  private _analyserNode: AnalyserNode;
  private _filterNodes: BiquadFilterNode[] = [];
  private _filterConfigs: AudioFilterConfig[] = [];

  private _volume: number = 0; // dB
  private _muted: boolean = false;
  private _solo: boolean = false;

  /** Connection to parent bus input */
  private _parentConnection: AudioNode | null = null;

  // Send nodes (aux sends to other buses)
  private _sends: Map<string, { gain: GainNode; targetBusId: string; enabled: boolean }> = new Map();

  // Metering
  private _peakLevel: number = 0;
  private _rmsLevel: number = 0;
  private _analyserBuffer: Float32Array<ArrayBuffer>;

  constructor(ctx: AudioContext, config: AudioBusConfig) {
    this._ctx = ctx;
    this.id = config.id;
    this.name = config.name;
    this.parentBusId = config.parentBusId;
    this._volume = config.volume;
    this._muted = config.muted;
    this._solo = config.solo;

    // Create audio graph: input -> filters -> gainNode -> muteNode -> analyser -> output
    this._gainNode = ctx.createGain();
    this._muteNode = ctx.createGain();
    this._analyserNode = ctx.createAnalyser();
    this._analyserNode.fftSize = 256;
    this._analyserBuffer = new Float32Array(new ArrayBuffer(this._analyserNode.frequencyBinCount * 4));

    // Set initial volume
    this._gainNode.gain.setValueAtTime(this._dbToLinear(this._volume), ctx.currentTime);
    this._muteNode.gain.setValueAtTime(this._muted ? 0 : 1, ctx.currentTime);

    // Build filter chain
    this._rebuildFilterChain(config.filters);
  }

  // ==========================================================================
  // PUBLIC API
  // ==========================================================================

  /** The input node (connect audio sources here) */
  get input(): AudioNode {
    return this._filterNodes.length > 0 ? this._filterNodes[0] : this._gainNode;
  }

  /** The output node (connect to parent bus or destination) */
  get output(): AudioNode {
    return this._analyserNode;
  }

  /** Volume in dB (-80 to +20) */
  get volume(): number { return this._volume; }
  set volume(db: number) {
    this._volume = Math.max(-80, Math.min(20, db));
    this._gainNode.gain.setTargetAtTime(
      this._dbToLinear(this._volume),
      this._ctx.currentTime,
      0.02,
    );
  }

  /**
   * Smoothly ramp volume to target dB over `duration` seconds.
   */
  rampVolume(targetDb: number, duration: number): void {
    this._volume = Math.max(-80, Math.min(20, targetDb));
    if (duration <= 0) {
      this._gainNode.gain.setTargetAtTime(
        this._dbToLinear(this._volume),
        this._ctx.currentTime,
        0.02,
      );
    } else {
      this._gainNode.gain.linearRampToValueAtTime(
        this._dbToLinear(this._volume),
        this._ctx.currentTime + duration,
      );
    }
  }

  /** Muted state */
  get muted(): boolean { return this._muted; }
  set muted(v: boolean) {
    this._muted = v;
    this._muteNode.gain.setTargetAtTime(v ? 0 : 1, this._ctx.currentTime, 0.01);
  }

  /** Solo state */
  get solo(): boolean { return this._solo; }
  set solo(v: boolean) { this._solo = v; }

  /** Volume as linear (0..1+) */
  get linearVolume(): number {
    return this._dbToLinear(this._volume);
  }

  /** Current metered peak level (0..1) */
  get peakLevel(): number { return this._peakLevel; }

  /** Current metered RMS level (0..1) */
  get rmsLevel(): number { return this._rmsLevel; }

  /**
   * Connect this bus output to a parent node.
   */
  connectTo(target: AudioNode): void {
    if (this._parentConnection) {
      this._analyserNode.disconnect(this._parentConnection);
    }
    this._parentConnection = target;
    this._analyserNode.connect(target);
  }

  /**
   * Disconnect from parent.
   */
  disconnect(): void {
    try {
      this._analyserNode.disconnect();
    } catch { /* no-op */ }
    this._parentConnection = null;
  }

  /**
   * Add a send to another bus.
   */
  addSend(targetBusId: string, level: number, enabled: boolean, targetInput: AudioNode): void {
    const gain = this._ctx.createGain();
    gain.gain.setValueAtTime(this._dbToLinear(level), this._ctx.currentTime);

    // Tap from after the gain/mute but before analyser
    this._muteNode.connect(gain);
    gain.connect(targetInput);

    this._sends.set(targetBusId, { gain, targetBusId, enabled });
    if (!enabled) {
      gain.gain.setValueAtTime(0, this._ctx.currentTime);
    }
  }

  /**
   * Remove a send.
   */
  removeSend(targetBusId: string): void {
    const send = this._sends.get(targetBusId);
    if (!send) return;
    try {
      send.gain.disconnect();
      this._muteNode.disconnect(send.gain);
    } catch { /* no-op */ }
    this._sends.delete(targetBusId);
  }

  /**
   * Update send level.
   */
  setSendLevel(targetBusId: string, level: number): void {
    const send = this._sends.get(targetBusId);
    if (!send) return;
    send.gain.gain.setTargetAtTime(
      send.enabled ? this._dbToLinear(level) : 0,
      this._ctx.currentTime,
      0.02,
    );
  }

  /**
   * Update filters.
   */
  setFilters(configs: AudioFilterConfig[]): void {
    this._rebuildFilterChain(configs);
  }

  /**
   * Smoothly transition filter parameters over `duration` seconds.
   * If the filter topology changes (different count or types), the chain is
   * rebuilt instantly and then parameters are ramped.
   */
  transitionFilters(configs: AudioFilterConfig[], duration: number): void {
    const enabledNew = configs.filter(c => c.enabled);
    const enabledOld = this._filterConfigs.filter(c => c.enabled);

    // Check if topology matches (same count & types in order)
    const topologyMatch =
      enabledNew.length === enabledOld.length &&
      enabledNew.every((c, i) => c.type === enabledOld[i].type);

    if (!topologyMatch || duration <= 0) {
      // Topology changed or instant — full rebuild
      this._rebuildFilterChain(configs);
      return;
    }

    // Same topology — ramp parameters smoothly
    this._filterConfigs = configs.map(c => ({ ...c }));
    const t = this._ctx.currentTime;
    let nodeIdx = 0;
    for (const config of configs) {
      if (!config.enabled) continue;
      const node = this._filterNodes[nodeIdx];
      if (node) {
        node.frequency.linearRampToValueAtTime(config.frequency, t + duration);
        node.Q.linearRampToValueAtTime(config.Q, t + duration);
        node.gain.linearRampToValueAtTime(config.gain, t + duration);
      }
      nodeIdx++;
    }
  }

  /** Current filter configs */
  get filters(): readonly AudioFilterConfig[] {
    return this._filterConfigs;
  }

  /**
   * Update metering values. Call periodically (e.g., on animation frame).
   */
  updateMetering(): void {
    this._analyserNode.getFloatTimeDomainData(this._analyserBuffer);

    let peak = 0;
    let sumSquared = 0;
    const len = this._analyserBuffer.length;

    for (let i = 0; i < len; i++) {
      const v = Math.abs(this._analyserBuffer[i]);
      if (v > peak) peak = v;
      sumSquared += this._analyserBuffer[i] * this._analyserBuffer[i];
    }

    this._peakLevel = peak;
    this._rmsLevel = Math.sqrt(sumSquared / len);
  }

  /**
   * Get current state for UI.
   */
  getState(): AudioBusState {
    return {
      id: this.id,
      name: this.name,
      volume: this._volume,
      muted: this._muted,
      solo: this._solo,
      peakLevel: this._peakLevel,
      rmsLevel: this._rmsLevel,
    };
  }

  /**
   * Get the bus config for serialization.
   */
  getConfig(): AudioBusConfig {
    return {
      id: this.id,
      name: this.name,
      volume: this._volume,
      muted: this._muted,
      solo: this._solo,
      parentBusId: this.parentBusId,
      sends: Array.from(this._sends.values()).map(s => ({
        targetBusId: s.targetBusId,
        level: 0,
        enabled: s.enabled,
      })),
      filters: [...this._filterConfigs],
    };
  }

  /**
   * Destroy and release all nodes.
   */
  destroy(): void {
    this.disconnect();
    for (const send of this._sends.values()) {
      try { send.gain.disconnect(); } catch { /* no-op */ }
    }
    this._sends.clear();
    for (const f of this._filterNodes) {
      try { f.disconnect(); } catch { /* no-op */ }
    }
    this._filterNodes = [];
  }

  // ==========================================================================
  // PRIVATE
  // ==========================================================================

  private _rebuildFilterChain(configs: AudioFilterConfig[]): void {
    // Disconnect old filters
    for (const f of this._filterNodes) {
      try { f.disconnect(); } catch { /* no-op */ }
    }
    this._filterNodes = [];
    this._filterConfigs = configs.map(c => ({ ...c }));

    // Build new filter chain
    const enabledFilters = configs.filter(c => c.enabled);

    if (enabledFilters.length === 0) {
      // No filters: gain -> mute -> analyser
      this._gainNode.disconnect();
      this._gainNode.connect(this._muteNode);
      this._muteNode.disconnect();
      this._muteNode.connect(this._analyserNode);
      return;
    }

    // Create filter nodes
    for (const config of enabledFilters) {
      const filter = this._ctx.createBiquadFilter();
      filter.type = config.type;
      filter.frequency.setValueAtTime(config.frequency, this._ctx.currentTime);
      filter.Q.setValueAtTime(config.Q, this._ctx.currentTime);
      filter.gain.setValueAtTime(config.gain, this._ctx.currentTime);
      this._filterNodes.push(filter);
    }

    // Chain: first filter -> ... -> last filter -> gain -> mute -> analyser
    // Input goes to first filter
    for (let i = 0; i < this._filterNodes.length - 1; i++) {
      this._filterNodes[i].connect(this._filterNodes[i + 1]);
    }

    // Last filter -> gain
    const lastFilter = this._filterNodes[this._filterNodes.length - 1];
    lastFilter.connect(this._gainNode);

    // gain -> mute -> analyser
    this._gainNode.disconnect();
    this._gainNode.connect(this._muteNode);
    this._muteNode.disconnect();
    this._muteNode.connect(this._analyserNode);
  }

  private _dbToLinear(db: number): number {
    if (db <= -80) return 0;
    return Math.pow(10, db / 20);
  }
}
